/**
 * @fileoverview
 * Permission Guard - Protects routes based on user permissions
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService, PermissionService } from '../services';
import { firstValueFrom } from 'rxjs';
import { timeout, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

/**
 * Permission Guard Factory
 * Usage: canActivate: [permissionGuard('users.create')]
 *        canActivate: [permissionGuard(['users.create', 'users.update'], 'any')]
 */
export const permissionGuard = (
  requiredPermissions: string | string[],
  mode: 'all' | 'any' = 'any'
): CanActivateFn => {
  return async (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ) => {
    const authService = inject(AuthService);
    const permissionService = inject(PermissionService);
    const router = inject(Router);

    // Check if user is authenticated first
    if (!authService.isAuthenticated()) {
      return router.createUrlTree(['/auth/login'], {
        queryParams: { returnUrl: state.url }
      });
    }

    // If permissions are empty, try to fetch them
    const currentPermissions = permissionService.permissions();
    if (currentPermissions.length === 0 && !permissionService.loading()) {
      try {
        await firstValueFrom(
          permissionService.fetchPermissions().pipe(
            timeout({ first: 5000 }),
            catchError(() => of([])) // Return empty array on error/timeout
          )
        );
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
        // Continue with empty permissions - will fail permission check
      }
    }

    // Convert single permission to array
    const permissions = Array.isArray(requiredPermissions)
      ? requiredPermissions
      : [requiredPermissions];

    // Check permissions based on mode
    const hasAccess = mode === 'all'
      ? authService.hasAllPermissions(permissions)
      : authService.hasAnyPermission(permissions);

    if (hasAccess) {
      return true;
    }

    // User doesn't have required permissions - redirect to dashboard or show error
    // You can customize this behavior
    return router.createUrlTree(['/dashboard']);
  };
};
